import React, { ComponentType, ReactNode } from 'react';

import { NativeResources, PermissionSource } from '@ariksa/inventory-core';
import { AccessPathKind, CanAccessSummary } from '@ariksa/inventory-core/api';
import { Box, HStack } from '@chakra-ui/react';
import { colorOpacity, customTheme } from 'theme';

import {
  PlusCountTag,
  StackedCell,
  Tag,
  WithResourceIcon,
} from 'components/DataDisplay';

export const policyTypeMapping = type => {
  switch (type) {
    case PermissionSource.IamPolicy:
      return 'IAM';
    case PermissionSource.ResourcePolicy:
      return 'Resource';
    default:
      return type;
  }
};

// Keep provider-specific identity rules and renderers outside the stateful view
// so IdentityPermissions only coordinates data, filters, and table state.
export const snowflakeIdentities: NativeResources[] = [
  NativeResources.SnowflakeUsers,
  NativeResources.SnowflakeRoles,
];

export const noPolicyType = { label: 'Policy Type', value: undefined };

const policyPaths: string[] = [
  AccessPathKind.ManagedPolicy,
  AccessPathKind.InlinePolicy,
  AccessPathKind.PermissionBoundary,
  AccessPathKind.ResourcePolicy,
];

export const getPathFilter = (path?: CanAccessSummary) => {
  const uuid = path?.permission_entity_uuid;
  if (!uuid) return {};
  if (path?.access_path === AccessPathKind.AssumedRole) {
    return { assume_role_uuid: uuid };
  }
  return policyPaths.includes(path?.access_path ?? '')
    ? { policy_uuid: uuid }
    : {};
};

export const isNarrowable = (path?: CanAccessSummary) =>
  !!Object.keys(getPathFilter(path)).length;

export const getAccessPathSummary = (paths: CanAccessSummary[]) => {
  const countOf = (kind: string) =>
    paths.filter(path => path.access_path === kind).length;
  const assumed = countOf(AccessPathKind.AssumedRole);
  const viaResource = countOf(AccessPathKind.ResourcePolicy);
  const direct = paths.length - assumed - viaResource;
  const total = `${paths.length} ${paths.length === 1 ? 'path' : 'paths'}`;
  const parts = [
    direct && `${direct} Direct Access`,
    assumed && `${assumed} via Assume Role`,
    viaResource && `${viaResource} via Resource Policy`,
  ].filter(Boolean);

  return parts.length < 2 ? total : `${total} (${parts.join(', ')})`;
};

export const createPermissionEntityRenderer = (
  getCloudNativeName: (type: string) => ReactNode,
  getCloudAgnosticName: (type: string) => ReactNode,
) => ({ row, value }) => (
  <WithResourceIcon resourceType={row?.permission_entity_native_name}>
    <StackedCell
      upper={value}
      lower={
        getCloudNativeName(row?.permission_entity_native_name) +
        ' / ' +
        getCloudAgnosticName(row?.permission_entity_agnostic_name)
      }
    />
  </WithResourceIcon>
);

export const renderServicesCell = ({ row }) => {
  const names: string[] = row?.services ?? [];
  const total = row?.total_service_count ?? 0;
  if (!names.length) return total || '-';
  const shown = names.slice(0, 2);
  const more = total - shown.length;
  const extraNames = names.slice(shown.length);

  // Use shared tags and expose hidden services through the overflow tooltip.
  return (
    <HStack spacing={1}>
      {shown.map(name => (
        <Tag
          key={name}
          label={name}
          styles={{
            tag: {
              bg: colorOpacity(customTheme.colors.primary, 0.2),
              border: 'none',
            },
          }}
        />
      ))}
      {more > 0 && (
        <PlusCountTag
          count={more}
          tooltip={
            extraNames.length
              ? extraNames.join(', ')
              : `${more} additional service${more === 1 ? '' : 's'}`
          }
        />
      )}
    </HStack>
  );
};

export const renderExcessPermissions = ({ row }) =>
  (row?.show_excess_counts
    ? (row?.total_permission_count ?? 0) - (row?.used_permission_count ?? 0)
    : '-') +
  ' / ' +
  (row?.total_permission_count ?? 0);

interface IdentityPermissionColumnOptions {
  isSnowflake: boolean;
  renderEntity: (props: any) => ReactNode;
  AccountCell: ComponentType<any>;
  renderHighPrivilegesCell: (props: any) => ReactNode;
}

export const getIdentityPermissionColumns = ({
  isSnowflake,
  renderEntity,
  AccountCell,
  renderHighPrivilegesCell,
}: IdentityPermissionColumnOptions) => {
  const name = {
    header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
    accessor: 'permission_entity_name',
    render: renderEntity,
  };

  const highPrivilegesService = [
    {
      header: 'High Privileges',
      accessor: 'crud_level_counts_high',
      render: renderHighPrivilegesCell,
    },
    {
      header: 'Services',
      accessor: 'total_service_count',
      align: 'left',
      render: renderServicesCell,
    },
  ];

  if (isSnowflake) {
    return [
      name,
      { header: 'Owner', accessor: 'permission_entity_owner', align: 'left' },
      {
        header: 'Type',
        accessor: 'permission_entity_managed_type',
        align: 'left',
      },
      {
        header: 'High Privileges',
        accessor: 'crud_level_counts_high',
        render: renderHighPrivilegesCell,
      },
      { header: 'Total Privileges', accessor: 'total_permission_count' },
    ];
  }

  return [
    {
      header: <Box pl="thLeftPaddingWithIcon">Granted by</Box>,
      accessor: 'permission_entity_name',
      render: renderEntity,
    },
    {
      header: 'Account',
      accessor: 'permission_entity_account',
      render: ({ row, value }) => (
        <AccountCell accountId={value} external={row?.external} />
      ),
      align: 'left',
    },
    ...highPrivilegesService,
    {
      header: '# Excess / # Permissions',
      render: renderExcessPermissions,
    },
  ];
};
